import { useEffect } from 'react';

/**
 * Lightweight SEO hook — sets document.title and key meta tags per page.
 * Used by blog posts and informational pages to provide per-route SEO.
 */
export const useSEO = ({ title, description, canonical, ogImage }) => {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (name, content, isProperty = false) => {
      if (!content) return null;
      const attr = isProperty ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      const prev = tag ? tag.getAttribute('content') : null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
      return prev;
    };

    const setLink = (rel, href) => {
      if (!href) return null;
      let tag = document.querySelector(`link[rel="${rel}"]`);
      const prev = tag ? tag.getAttribute('href') : null;
      if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        document.head.appendChild(tag);
      }
      tag.setAttribute('href', href);
      return prev;
    };

    const prevDesc = setMeta('description', description);
    const prevOgTitle = setMeta('og:title', title, true);
    const prevOgDesc = setMeta('og:description', description, true);
    const prevOgUrl = setMeta('og:url', canonical, true);
    const prevOgImage = ogImage ? setMeta('og:image', ogImage, true) : null;
    const prevTwTitle = setMeta('twitter:title', title, true);
    const prevTwDesc = setMeta('twitter:description', description, true);
    const prevCanonical = setLink('canonical', canonical);

    return () => {
      document.title = prevTitle;
      if (prevDesc !== null) setMeta('description', prevDesc);
      if (prevOgTitle !== null) setMeta('og:title', prevOgTitle, true);
      if (prevOgDesc !== null) setMeta('og:description', prevOgDesc, true);
      if (prevOgUrl !== null) setMeta('og:url', prevOgUrl, true);
      if (prevOgImage !== null) setMeta('og:image', prevOgImage, true);
      if (prevTwTitle !== null) setMeta('twitter:title', prevTwTitle, true);
      if (prevTwDesc !== null) setMeta('twitter:description', prevTwDesc, true);
      if (prevCanonical !== null) setLink('canonical', prevCanonical);
    };
  }, [title, description, canonical, ogImage]);
};
